/** @format */

import React from "react";
import { Box, IconButton, CircularProgress } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

const MAX_ROWS = 4;
const LINE_HEIGHT = 22;
const MIN_HEIGHT = 42;
const MAX_HEIGHT = MAX_ROWS * LINE_HEIGHT + 16;

const ChatInput = ({ onSend, isLoading, isReady }) => {
  const [value, setValue] = React.useState("");
  const textareaRef = React.useRef(null);

  const adjustHeight = () => {
    const t = textareaRef.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${Math.min(
      Math.max(t.scrollHeight, MIN_HEIGHT),
      MAX_HEIGHT
    )}px`;
  };

  const resetHeight = () => {
    const t = textareaRef.current;
    if (t) t.style.height = `${MIN_HEIGHT}px`;
  };

  const handleChange = (e) => {
    setValue(e.target.value);
    adjustHeight();
  };

  const handleSend = () => {
    const text = value.trim();
    if (!text || isLoading || !isReady) return;
    onSend(text);
    setValue("");
    resetHeight();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !isLoading && isReady && value.trim().length > 0;
  const placeholder = !isReady
    ? "Connecting to ProPilot…"
    : isLoading
    ? "Waiting for response…"
    : "Ask about your shipments…";

  const kbdSx = {
    px: 0.6,
    py: 0.15,
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    fontSize: 10,
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#fff",
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1, md: 1.5 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5 }}>
        <Box sx={{ flex: 1, position: "relative" }}>
          <Box
            component="textarea"
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading || !isReady}
            placeholder={placeholder}
            rows={1}
            sx={{
              width: "100%",
              fontFamily: "inherit",
              // ≥16px on touch devices so iOS Safari doesn't auto-zoom on focus.
              fontSize: { xs: 16, md: 14 },
              color: "#1e293b",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              px: 2,
              py: 1.25,
              lineHeight: `${LINE_HEIGHT}px`,
              resize: "none",
              transition: "all 150ms ease",
              outline: "none",
              minHeight: `${MIN_HEIGHT}px`,
              maxHeight: `${MAX_HEIGHT}px`,
              boxSizing: "border-box",
              "&::placeholder": { color: "#94a3b8" },
              "&:focus": {
                borderColor: "#a5b4fc",
                backgroundColor: "#fff",
                boxShadow: "0 0 0 3px rgba(99,102,241,0.15)",
              },
              "&:disabled": {
                opacity: 0.7,
                cursor: "not-allowed",
                backgroundColor: "#f1f5f9",
              },
            }}
          />
        </Box>
        <IconButton
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            flexShrink: 0,
            backgroundColor: canSend ? "#6366f1" : "#e2e8f0",
            color: canSend ? "#fff" : "#94a3b8",
            "&:hover": {
              backgroundColor: canSend ? "#4f46e5" : "#e2e8f0",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
              color: "#94a3b8",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} thickness={5} sx={{ color: "#94a3b8" }} />
          ) : (
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
      <Box
        sx={{
          fontSize: 10,
          color: "#94a3b8",
          mt: 0.85,
          ml: 0.25,
          // Keyboard hints are meaningless on touch — reclaim the row on phones.
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        <Box component="span">Press</Box>
        <Box component="kbd" sx={kbdSx}>
          Enter
        </Box>
        <Box component="span">to send ·</Box>
        <Box component="kbd" sx={kbdSx}>
          Shift + Enter
        </Box>
        <Box component="span">for new line</Box>
      </Box>
    </Box>
  );
};

export default ChatInput;
